import { Bot, ShieldCheck } from 'lucide-react';

import { AttachmentBubble } from '@/components/molecules/AttachmentBubble';
import { MessageContent } from '@/components/molecules/MessageContent';
import { useUsd } from '@/hooks/useUsd';
import { formatClockTime } from '@/utils/datetime';

import { BUBBLE_CLASS, BUBBLE_MAX_WIDTH, ROLE_SIDE, ROW_CLASS, TAIL_CLASS } from './ChatBubble.constants';
import type { ChatBubbleProps } from './ChatBubble.types';

const Avatar = ({ msg }: { msg: ChatBubbleProps['msg'] }) => {
	if (msg.role === 'ai')
		return (
			<div className="inline-flex h-2xl w-2xl shrink-0 items-center justify-center rounded-full border border-purple-600 bg-purple-500 text-white">
				<Bot className="h-[14px] w-[14px]" />
			</div>
		);
	if (msg.role === 'system')
		return (
			<div className="inline-flex h-2xl w-2xl shrink-0 items-center justify-center rounded-full border border-subtle bg-white text-subtle">
				<ShieldCheck className="h-[14px] w-[14px]" />
			</div>
		);
	return (
		<div className="inline-flex h-2xl w-2xl shrink-0 items-center justify-center rounded-full border border-subtle bg-grey-25 text-[11px] font-semibold leading-none text-default">
			{msg.authorInitials ?? 'U'}
		</div>
	);
};

export const ChatBubble = ({ msg, isFirstOfGroup, isLastOfGroup, showMetadata }: ChatBubbleProps) => {
	const usd = useUsd();
	const side = ROLE_SIDE[msg.role];
	const hasText = msg.content.trim().length > 0;
	const attachments = msg.attachments ?? [];

	if (side === 'center')
		return (
			<li className="flex justify-center" data-role={msg.role}>
				<p className="max-w-[70%] text-center text-[12px] italic leading-relaxed text-subtle">
					{hasText ? <MessageContent content={msg.content} /> : null}
				</p>
			</li>
		);

	return (
		<li className={`flex items-end gap-xs ${ROW_CLASS[side]}`} data-role={msg.role} data-side={side}>
			{side === 'left' && <div className="w-2xl shrink-0">{isLastOfGroup && <Avatar msg={msg} />}</div>}
			<div className={`flex min-w-0 flex-col gap-3xs ${BUBBLE_MAX_WIDTH} ${side === 'right' ? 'items-end' : ''}`}>
				{isFirstOfGroup && (
					<span className="px-2xs text-[11px] font-semibold leading-none text-subtle">{msg.authorName}</span>
				)}
				<div
					className={`w-full rounded-lg px-md py-sm text-[13px] leading-relaxed ${BUBBLE_CLASS[side]} ${isLastOfGroup ? TAIL_CLASS[side] : ''}`}
				>
					{attachments.length > 0 && <AttachmentBubble attachments={attachments} />}
					{hasText && (
						<div className={attachments.length > 0 ? 'mt-2xs' : ''}>
							<MessageContent content={msg.content} />
						</div>
					)}
					<div className="mt-3xs flex items-center justify-end gap-2xs text-[10px] leading-none text-subtle">
						{showMetadata && msg.model && <span className="font-mono">{msg.model}</span>}
						{showMetadata && msg.costUsd != null && <span className="font-mono">{usd.unit(msg.costUsd)}</span>}
						<time dateTime={msg.createdAt} className="tabular-nums">
							{formatClockTime(msg.createdAt)}
						</time>
					</div>
				</div>
			</div>
			{side === 'right' && <div className="w-2xl shrink-0">{isLastOfGroup && <Avatar msg={msg} />}</div>}
		</li>
	);
};
